"use client";

import * as React from "react";
import { router } from "@inertiajs/react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type BoozeImageOption = {
  id: number;
  thumbUrl?: string;
  label?: string | null;
};

export function displayBoozeImageLabel(img: BoozeImageOption) {
  const t = img.label?.trim();
  return t || `Image #${img.id}`;
}

type PickerProps = {
  images: BoozeImageOption[];
  selectedId: string;
  onSelectId: (id: string) => void;
  disabled?: boolean;
  noneOptionLabel: string;
  searchPlaceholder?: string;
};

export function BoozeImagePickerCombobox({
  images,
  selectedId,
  onSelectId,
  disabled,
  noneOptionLabel,
  searchPlaceholder = "Search by label or id…",
}: PickerProps) {
  const [open, setOpen] = React.useState(false);

  const selected = selectedId
    ? images.find((i) => String(i.id) === selectedId)
    : undefined;
  const summary = selectedId
    ? selected
      ? displayBoozeImageLabel(selected)
      : `Image #${selectedId}`
    : noneOptionLabel;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-auto min-h-9 w-full justify-between py-2 font-normal"
        >
          <span className="flex min-w-0 items-center gap-2">
            {selected?.thumbUrl ? (
              <img
                src={selected.thumbUrl}
                alt=""
                className="size-8 shrink-0 rounded object-cover"
              />
            ) : null}
            <span className="truncate text-left">{summary}</span>
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>No image found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__bh_img_none__"
                keywords={["none", "clear", "skip", "upload"]}
                onSelect={() => {
                  onSelectId("");
                  setOpen(false);
                }}
              >
                {noneOptionLabel}
                <Check
                  className={cn(
                    "ml-auto size-4 shrink-0",
                    !selectedId ? "opacity-100" : "opacity-0",
                  )}
                />
              </CommandItem>
              {images.map((img) => {
                const idStr = String(img.id);
                const text = displayBoozeImageLabel(img);
                return (
                  <CommandItem
                    key={img.id}
                    value={`${idStr}-${text}`}
                    keywords={[idStr, text, img.label ?? ""]}
                    onSelect={() => {
                      onSelectId(idStr);
                      setOpen(false);
                    }}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      {img.thumbUrl ? (
                        <img
                          src={img.thumbUrl}
                          alt=""
                          className="size-8 shrink-0 rounded object-cover"
                        />
                      ) : null}
                      <span className="truncate">{text}</span>
                    </span>
                    <Check
                      className={cn(
                        "ml-auto size-4 shrink-0",
                        selectedId === idStr ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

type LabelEditorProps = {
  imageId: string | null | undefined;
  images: BoozeImageOption[];
  disabled?: boolean;
};

export function BoozeImageLabelEditor({
  imageId,
  images,
  disabled,
}: LabelEditorProps) {
  const selected = imageId
    ? images.find((i) => String(i.id) === imageId)
    : undefined;
  const [draft, setDraft] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const imagesFingerprint = React.useMemo(
    () => images.map((i) => `${i.id}:${i.label ?? ""}`).join("|"),
    [images],
  );

  React.useEffect(() => {
    const img = images.find((i) => String(i.id) === imageId);
    setDraft((img?.label ?? "").trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when server list content changes, not array identity
  }, [imageId, imagesFingerprint]);

  if (!imageId) return null;

  // No nested <form> here: this control sits inside the booze new/edit <form>, and HTML
  // disallows nested forms. Browsers repair the tree by closing the outer form, so a submit
  // could hit the main booze PUT (which does not update BoozeImage#label) instead of patch.
  function save() {
    if (saving) return;
    setSaving(true);
    router.patch(
      `/booze_images/${imageId}`,
      { label: draft },
      {
        preserveScroll: true,
        onSuccess: () => {
          // router.reload() forces preserveState: true; explicit GET + partial props refreshes boozeImages reliably.
          router.visit(`${window.location.pathname}${window.location.search}`, {
            method: "get",
            only: ["boozeImages"],
            preserveScroll: true,
            replace: true,
          });
        },
        onFinish: () => setSaving(false),
      },
    );
  }

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
      <div className="min-w-0 flex-1 space-y-1.5">
        <Label
          htmlFor={`booze-image-label-${imageId}`}
          className="text-xs font-normal text-muted-foreground"
        >
          Label for this image
        </Label>
        <Input
          id={`booze-image-label-${imageId}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (!disabled && !saving) save();
            }
          }}
          disabled={disabled || saving}
          placeholder="Short tag shown when picking this image…"
          maxLength={255}
          autoComplete="off"
        />
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={disabled || saving}
        className="shrink-0"
        onClick={save}
      >
        {saving ? "Saving…" : "Save label"}
      </Button>
    </div>
  );
}
